import { Button } from 'blaze-ui/Button'
import { FileEdit2 } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { EditCaptionModal, EditCaptionSavePayload } from 'components/EditCaptionModal/EditCaptionModal'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { useQueryClient } from 'react-query'
import { updateCalendarItemInCache } from 'hooks/queries/calendar/utils'
import { ButtonSize, ButtonVariant } from 'blaze-ui/Button/Types'
import { Icon } from 'blaze-ui/icons/Types'
import { StyleProps } from 'blaze-ui/StyleProps'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUpdateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useUpdateScheduledPostTarget'
import { getCrossPostGroupForChannel } from '../utils'

export const EditCaptionButton = ({
  scheduledPost,
  almanacFileKey,
  variant = 'tertiary',
  size = 'md',
  frontIcon = FileEdit2,
  justifyContent = 'flex-start',
  fullWidth = true,
  vertical = true,
  shouldHideCaptionField = false,
  label = 'Caption',
  isDisabled = false,
}: {
  scheduledPost: ScheduledPostShow
  almanacFileKey: string
  variant?: ButtonVariant
  size?: ButtonSize
  frontIcon?: Icon
  justifyContent?: StyleProps['justifyContent']
  fullWidth?: boolean
  vertical?: boolean
  shouldHideCaptionField?: boolean
  label?: string
  isDisabled?: boolean
}) => {
  const workspaceId = useCurrentWorkspaceId()
  const { openModal } = useModals()
  const queryClient = useQueryClient()
  const { mutateAsync: updateScheduledPost } = useUpdateScheduledPost()
  const { mutateAsync: updateScheduledPostTarget } = useUpdateScheduledPostTarget()
  const dynamicCaptionProviders = getCrossPostGroupForChannel(scheduledPost?.source_channel)

  const handleUpdateCaption = async (payload: EditCaptionSavePayload) => {
    if (typeof payload === 'string') {
      const updatedScheduledPost = await updateScheduledPost({ id: scheduledPost.id, params: { content: payload } })

      updateCalendarItemInCache(
        queryClient,
        almanacFileKey,
        (item) => ({
          ...item,
          scheduled_post: updatedScheduledPost,
          content: updatedScheduledPost.content ?? null,
        }),
        workspaceId,
      )
      return
    }

    const updatedTargets = new Map<number, (typeof scheduledPost.scheduled_post_targets)[number]>()

    for (const target of payload.targets) {
      const content = payload.captionsByTargetId[target.id] ?? payload.captionsByProvider[target.provider]

      if (content === undefined) continue

      const location = payload.locationsByTargetId?.[target.id]
      const tags = payload.tagsByTargetId?.[target.id]

      const updatedTarget = await updateScheduledPostTarget({
        scheduled_post_id: scheduledPost.id,
        target_id: target.id,
        target_overrides: {
          ...target.target_overrides,
          content,
          ...(location !== undefined && { location }),
          ...(tags !== undefined && { tags }),
        },
      })

      updatedTargets.set(updatedTarget.id, updatedTarget)
    }

    const nextDynamicTargets = payload.targets.map((target) => updatedTargets.get(target.id) ?? target)
    const nextTargets = [
      ...scheduledPost.scheduled_post_targets.filter((target) => !dynamicCaptionProviders.includes(target.provider)),
      ...nextDynamicTargets,
    ]

    updateCalendarItemInCache(
      queryClient,
      almanacFileKey,
      (item) => {
        if ('scheduled_post' in item) {
          return {
            ...item,
            scheduled_post_targets: nextTargets,
            scheduled_post: item.scheduled_post
              ? {
                  ...item.scheduled_post,
                  scheduled_post_targets: nextTargets,
                }
              : item.scheduled_post,
          }
        }

        return {
          ...item,
          scheduled_post_targets: nextTargets,
        }
      },
      workspaceId,
    )
  }

  return (
    <Button
      variant={variant}
      size={size}
      frontIcon={frontIcon}
      justifyContent={justifyContent}
      fullWidth={fullWidth}
      vertical={vertical}
      isDisabled={shouldHideCaptionField || isDisabled}
      onPress={() =>
        openModal(
          EditCaptionModal,
          {
            scheduledPostId: scheduledPost.id,
            caption: scheduledPost.content || '',
            scheduledPostTargets: scheduledPost.scheduled_post_targets,
            onSave: handleUpdateCaption,
            sourceChannel: scheduledPost?.source_channel,
          },
          { newStack: true },
        )
      }
    >
      {label}
    </Button>
  )
}
